import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IPreferenceState } from './preference-state';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { SessionService } from '../session/session.service';
import { PreferenceDataService } from '@core/data/api/preference/preference-data.service';
import {
    FetchPreferenceAction,
    MergePreferenceAction,
    SavePreferenceAction,
    SetDeliveryListPreferenceAction,
    SetLocationPreferenceAction,
    SetMultiLocationPreferenceAction,
    SetPreferenceAction,
    SetReportsPreferenceAction
} from './preference-actions';
import {
    PREFERENCE_DELIVERIES_SET,
    PREFERENCE_FETCH,
    PREFERENCE_IHDSITES_SET,
    PREFERENCE_LIVEORDERS_SET,
    PREFERENCE_LOCATION_SET,
    PREFERENCE_MERGE,
    PREFERENCE_MULTI_LOCATION_SET,
    PREFERENCE_REPORTS_SET,
    PREFERENCE_SAVE,
    PREFERENCE_SET
} from '../action-types';
import {
    Observable,
    debounceTime,
    forkJoin,
    from,
    mergeMap,
    of,
    switchMap, tap, withLatestFrom } from 'rxjs';
import { getPreferenceState } from './preference-selectors';
import { getCompany, getUser } from '../session/session-selectors';
import { ossFilterAuth } from '@core/operators/filter-auth.operator';
import { FEATURE_SAVE_PREFERENCE } from '@core/auth/feature-id';
import { Company } from '@core/models/common/company';
import { User } from '@core/models/user/user';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { $log } from '@core/utils/logger';
import { NoAction } from '../shared/actions/no-action';
import { isEmptyObj } from '@core/utils/object';
import { AuthService } from '../auth/auth.service';
import { STORE_COMPANY_PREFS, STORE_USER_PREFS } from '@core/local-storage/local-storage';
import { isAuthenticated } from '../auth/auth-selectors';

@Injectable()
export class PreferenceEffects {

    constructor(private localStorageService: LocalStorageService,
                private authService: AuthService,
                private store$: Store<IPreferenceState>,
                private sessionService: SessionService,
                private preferenceDataService: PreferenceDataService,
                private actions$: Actions) {
    }

    savePreference$ = createEffect(() => 
        this.actions$.pipe(
            ofType<SavePreferenceAction>(PREFERENCE_SAVE),
            debounceTime(500),
            withLatestFrom(
                this.store$.select(getPreferenceState),
                this.store$.select(getUser),
                this.store$.select(getCompany)
            ),
            ossFilterAuth(FEATURE_SAVE_PREFERENCE),
            switchMap(([_, preference, user, company]:
                [SavePreferenceAction, any, User, Company]) => {

                    let requests$: Observable<any>[] = [];

                    if (preference) {
                        if (
                            preference.user &&
                            !isEmptyObj(preference.user[user.id])
                        ) {
                            requests$.push(
                                this.preferenceDataService.saveUserPrefs(
                                    {
                                        prefs: preference.user[user.id],
                                    },
                                    user.id
                                )
                            );
                        }
    
                        if (
                            preference.company &&
                            !isEmptyObj(
                                preference.company[company.id]
                            )
                        ) {
                            requests$.push(
                                this.preferenceDataService.saveCompanyPrefs(
                                    {
                                        prefs:
                                            preference.company[
                                                company.id
                                            ],
                                    },
                                    company.id
                                )
                            );
                        }
                    }
    
                    return forkJoin(requests$).pipe(
                        mergeMap(([userPrefs, companyPrefs]) => {
                            return [];
                        }),
                        ossCatchError(([actions, error]) => {
                            return from(actions);
                        })
                    );
            }),
            ossCatchError(([actions, error]) => {
                $log.warn('error in save preference', error);
                //  log and silently ignore the error
                return of(new NoAction());
            })
        )
    );

    fetchPreference = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchPreferenceAction>(PREFERENCE_FETCH),
            withLatestFrom(
                this.store$.select(getCompany),
                this.store$.select(getUser),
                this.authService.isAuthenticated()
            ),
            switchMap(([
                _, company, user, isAuthenticated
            ]: [
                FetchPreferenceAction,
                Company,
                User, 
                boolean
            ]) => {
                let requests$: Observable<any>[] = [];

                if (isAuthenticated) {
                    if (user) {
                        requests$.push(
                            this.preferenceDataService.fetchUserPrefs(user.id)
                        );
                    }

                    if (company) {
                        requests$.push(
                            this.preferenceDataService.fetchCompanyPrefs(
                                company.id
                            )
                        );
                    }
                }

                // this scenario would happen when the fetchPreference was dispatched during login but a 401 resulted in logout
                // by the time it got to this action and so the requests is empty
                if (requests$.length) {
                    return forkJoin(requests$).pipe(
                        mergeMap(([userPrefs, companyPrefs]) => {

                            const prefs = {};

                            // for new users where there was no preference, it could be an empty object
                            // and we do not want to override with the local preference that would have been determined
                            // when the fetch was happening
                            if (
                                userPrefs &&
                                userPrefs.prefs &&
                                !isEmptyObj(userPrefs.prefs)
                            ) {
                                prefs['user'] = {
                                    [user.id]: userPrefs.prefs,
                                };
                            }

                            if (
                                companyPrefs &&
                                companyPrefs.prefs &&
                                !isEmptyObj(companyPrefs.prefs)
                            ) {
                                prefs['company'] = {
                                    [company.id]: companyPrefs.prefs,
                                };
                            }

                            return [new MergePreferenceAction(prefs)];
                        }),
                        ossCatchError(([actions, error]) => {
                            // if the fetch fails don't do anything, the back up is the localstorage
                            return of(new NoAction());
                        })
                    );
                } else {
                    return of(new NoAction());
                }
            })
        )
    );

    setLocalStoragePreference$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<MergePreferenceAction>(PREFERENCE_MERGE),
            withLatestFrom(this.store$.select(getPreferenceState)),
            tap(([_, preference]) => {

                if (preference?.user) {
                    this.localStorageService.setItem(STORE_USER_PREFS, preference.user);
                }

                if (preference?.company) {
                    this.localStorageService.setItem(STORE_COMPANY_PREFS, preference.company);
                }

            })
        );
    }, { dispatch: false });

    preferenceChange$ = createEffect(() => 
        this.actions$.pipe(
            ofType<
                SetPreferenceAction |
                SetLocationPreferenceAction |
                SetMultiLocationPreferenceAction | 
                SetDeliveryListPreferenceAction |
                SetReportsPreferenceAction
            >(
                PREFERENCE_SET,
                PREFERENCE_LOCATION_SET,
                PREFERENCE_MULTI_LOCATION_SET,
                PREFERENCE_DELIVERIES_SET,
                PREFERENCE_LIVEORDERS_SET,
                PREFERENCE_IHDSITES_SET,
                PREFERENCE_REPORTS_SET
            ),
            withLatestFrom(
                this.store$.select(getPreferenceState),
                this.store$.select(getUser),
                this.store$.select(isAuthenticated)
            ),
            mergeMap(([action, preferenceState, sessionUser, isAuthenticated]) => {
                const actions: Action[] = [];

                if (preferenceState.user) {
                    this.localStorageService.setItem(
                        STORE_USER_PREFS,
                        preferenceState.user
                    );
                }

                if (preferenceState.company) {
                    this.localStorageService.setItem(
                        STORE_COMPANY_PREFS,
                        preferenceState.company
                    );
                }
    
                if (preferenceState && isAuthenticated && sessionUser && !(action.payload as any).skipExternalUpdate) {
                    actions.push(new SavePreferenceAction());
                }
    
                return actions;
            })
        )
    );
}